import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import {
  gridListStyle,
  gridListHeadStyle,
  gridListFiltersStyle,
  gridListCardStyle,
  gridListDataStyle,
  onCellClickStyle,
  dashboardGridListCardStyle,
  dashboardGridListRowStyle,
  dashboardGridListStyle,
  dashboardGridListDataStyle,
  gridListLoadingStyle,
} from './GridList.style';
import { GridListPropsType } from './GridList.types';
import useGridList from './GrildList.hook';
import ContentCard from 'components/ContentCard';
import GridListPagination from 'components/GridListPagination/GridListPagination';
import Heading from 'components/Heading';
import Box from 'yoda-ui/components/Box';
import Button from 'yoda-ui/components/Button';
import { ButtonType } from 'yoda-ui/components/Button/Button.types';
import DataGrid from 'yoda-ui/components/DataGrid';

const GridList = <T extends Record<string, unknown>>(props: GridListPropsType<T>) => {
  const {
    title,
    columnConfig,
    rows,
    newElementButton,
    customFilters,
    loading,
    getRowId,
    getRowClassName,
    rowCountState,
    onPageChange,
    onPageSizeChange,
    disableColumnMenu,
    hideFooterPagination = false,
    onCellClick,
    isOnDashboard = false,
    sx,
  } = props;

  const {
    pageSize,
    setPageSize,
    getRowSpacing,
  } = useGridList();

  const computedGridListDataStyle = onCellClick ? { ...gridListDataStyle(pageSize), ...onCellClickStyle } : gridListDataStyle(pageSize);
  const cardStyle = isOnDashboard
    ? dashboardGridListCardStyle
    : gridListCardStyle;
  const dataGridStyle = {
    ...computedGridListDataStyle,
    ...(isOnDashboard ? { ...dashboardGridListRowStyle, ...dashboardGridListDataStyle } : {}),
    ...sx,
  };
  const dataGridListStyle = {
    ...gridListStyle,
    ...(isOnDashboard ? dashboardGridListStyle : {}),
    ...(loading ? gridListLoadingStyle : {}),
    ...sx,
  };

  const theme = useTheme();

  return (
    <Box sx={ dataGridListStyle }>
      {
        title
        && <Box sx={ gridListHeadStyle }>
          <Box>
            <Heading>
              { title }
            </Heading>
          </Box>
          {
            (() => {
              if (newElementButton?.newElementClick && newElementButton?.newElementLabel) {
                return <Button
                  buttonType={ ButtonType.home }
                  onClick={ newElementButton.newElementClick }
                  disabled={ newElementButton?.newElementDisabled || false }
                  style={ { marginBottom: '32px' } }
                >
                  { newElementButton.newElementLabel }
                  <AddIcon sx={ { fontSize: '24px' } } />
                </Button>;
              }
              return null;
            })()
          }
        </Box>
      }
      {
        customFilters
        && <Box sx={ gridListFiltersStyle(theme) }>
          { customFilters }
        </Box>
      }
      <ContentCard sx={ cardStyle }>
        <DataGrid
          sx={ dataGridStyle as SxProps<Theme> }
          getRowId={ getRowId }
          loading={ loading }
          rowCount={ rowCountState }
          rowsPerPageOptions={ [10, 20, 50] }
          pagination
          pageSize={ pageSize }
          paginationMode="server"
          onPageChange={ onPageChange }
          onPageSizeChange={ onPageSizeChange }
          disableColumnMenu={ disableColumnMenu }
          getRowSpacing={ getRowSpacing }
          getRowClassName={ getRowClassName }
          components={
            {
              Pagination: () => {
                return <GridListPagination pageSize={ pageSize } setPageSize={ setPageSize } />;
              },
            }
          }
          rows={ rows }
          columns={ columnConfig }
          disableColumnSelector
          hideFooterPagination={ hideFooterPagination }
          onCellClick={ onCellClick }
          autoHeight
        />
      </ContentCard>
    </Box>
  );
};

export default GridList;
